/**
 * R3 — Comparison mode. Side-by-side trajectories with locked axes +
 * synced hover. Per CD spec:
 *   - Max 3 panes (sharper tradeoffs at 3+).
 *   - "Lock Y-axis + time" toggle on by default — comparing on the same
 *     scale is the operator's most-common need; independent axes is for
 *     baseline-style "shape" comparisons.
 *   - Baseline is just another comparable (dashed line + "reference"
 *     label); no separate mode.
 *   - Hover any pane → all panes highlight the same timepoint.
 */

import { useMemo, useState } from 'react';
import { FamilyGlyph, TierPip } from './primitives';
import type { Comparable, PathogenFamily } from './types';
import { FAM } from './sample-data';

export interface ComparisonProps {
  comparables: readonly Comparable[];
  /** Initial selected ids (max 3). */
  initialIds?: readonly string[];
}

export function Comparison({ comparables, initialIds = ['c1', 'c2', 'c3'] }: ComparisonProps) {
  const [picked, setPicked] = useState<readonly string[]>(initialIds.slice(0, 3));
  const [sync, setSync] = useState(true);
  const [hi, setHi] = useState<number | null>(null);

  const cmps = picked.map(id => comparables.find(c => c.id === id)).filter((c): c is Comparable => !!c);
  const maxLen = Math.max(0, ...cmps.map(c => c.data.length));
  const globalMax = sync ? Math.max(0, ...cmps.flatMap(c => c.data)) : null;

  return (
    <div>
      <header style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, marginBottom: 14, flexWrap: 'wrap',
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 680 }}>Comparison</h2>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-subtle)', marginTop: 3 }}>
            {cmps.length} of max 3 · {sync ? 'synced axes' : 'independent axes'} · hover syncs the timepoint
          </div>
        </div>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-muted)',
          cursor: 'pointer',
        }}>
          <input type="checkbox" checked={sync} onChange={e => setSync(e.target.checked)} />
          Lock Y-axis + time
        </label>
      </header>

      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.max(1, cmps.length)}, minmax(0, 1fr))`,
        gap: 12,
      }}>
        {cmps.map(c => (
          <ComparePane
            key={c.id}
            c={c}
            max={sync && globalMax != null ? globalMax : Math.max(...c.data)}
            hi={hi}
            setHi={setHi}
            maxLen={maxLen}
            onRemove={cmps.length > 1 ? () => setPicked(picked.filter(x => x !== c.id)) : undefined}
          />
        ))}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        marginTop: 14, padding: '8px 0', borderTop: '1px solid var(--bm-border)',
      }}>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 700, letterSpacing: '.4px',
          color: 'var(--bm-text-muted)',
        }}>Compare to</span>
        {comparables.filter(c => !picked.includes(c.id)).map(c => (
          <button
            key={c.id}
            type="button"
            disabled={picked.length >= 3}
            onClick={() => picked.length < 3 && setPicked([...picked, c.id])}
            style={{
              padding: '3px 8px', borderRadius: 999,
              fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
              border: '1px solid var(--bm-border)',
              background: 'var(--bm-bg-subtle)',
              color: picked.length >= 3 ? 'var(--bm-text-subtle)' : 'var(--bm-text-muted)',
              cursor: picked.length >= 3 ? 'not-allowed' : 'pointer',
              opacity: picked.length >= 3 ? 0.5 : 1,
            }}
          >+ {c.label} <span style={{ opacity: 0.6 }}>{c.sub}</span></button>
        ))}
        {picked.length >= 3 && (
          <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>
            max 3 panes — remove one to add another
          </span>
        )}
        <span style={{
          marginLeft: 'auto',
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)',
        }}>baseline is just another comparable — no separate mode</span>
      </div>
    </div>
  );
}

function ComparePane({
  c, max, hi, setHi, maxLen, onRemove,
}: {
  c: Comparable;
  max: number;
  hi: number | null;
  setHi: (i: number | null) => void;
  maxLen: number;
  onRemove?: () => void;
}) {
  const W = 300, H = 150;
  const padL = 38, padR = 8, padT = 10, padB = 22;
  const pw = W - padL - padR;
  const ph = H - padT - padB;
  const xOf = (i: number) => padL + (i / Math.max(1, maxLen - 1)) * pw;
  const yOf = (v: number) => padT + ph - (v / Math.max(1, max)) * ph;
  const path = c.data.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xOf(i)} ${yOf(v)}`).join(' ');
  const areaPath = `${path} L ${xOf(c.data.length - 1)} ${padT + ph} L ${xOf(0)} ${padT + ph} Z`;

  const onMove = useMemo(() => (e: React.MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * W;
    setHi(Math.max(0, Math.min(maxLen - 1, Math.round(((x - padL) / pw) * (maxLen - 1)))));
  }, [maxLen, pw, setHi]);

  const hv = hi != null && hi < c.data.length ? c.data[hi] : null;
  const family = (FAM[c.pathogen] ?? 'respiratory') as PathogenFamily;

  return (
    <div style={{
      background: c.baseline ? 'var(--bm-bg)' : 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-sm)',
      overflow: 'hidden',
    }}>
      <header style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px', borderBottom: '1px solid var(--bm-border)',
      }}>
        <FamilyGlyph family={family} color={c.color} size={15} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--bm-text)' }}>{c.label}</div>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10, color: 'var(--bm-text-subtle)' }}>
            {c.sub}{c.baseline ? ' · reference' : ''}
          </div>
        </div>
        {!c.baseline && <TierPip tier={c.tier} small />}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="remove pane"
            style={{
              background: 'transparent', border: '1px solid var(--bm-border)',
              color: 'var(--bm-text-muted)', borderRadius: 4,
              width: 22, height: 22, cursor: 'pointer', fontSize: 12, lineHeight: 1,
            }}
          >×</button>
        )}
      </header>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', display: 'block', cursor: 'crosshair' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHi(null)}
        role="img"
        aria-label={`${c.label} trajectory`}
      >
        {[0, 0.5, 1].map((g, k) => (
          <line key={k}
            x1={padL} x2={W - padR}
            y1={padT + ph * g} y2={padT + ph * g}
            stroke="var(--bm-plot-grid)" />
        ))}
        <text x={padL - 6} y={padT + 4} textAnchor="end"
          fontFamily="var(--bm-font-mono)" fontSize="8.5" fill="var(--bm-text-subtle)">
          {Math.round(max)}
        </text>
        <path d={areaPath} fill={c.color} opacity="0.1" />
        <path d={path} fill="none" stroke={c.color} strokeWidth="2"
          strokeDasharray={c.baseline ? '4 3' : '0'} />
        {hv != null && (
          <>
            <line x1={xOf(hi!)} x2={xOf(hi!)} y1={padT} y2={padT + ph}
              stroke="var(--bm-text)" strokeDasharray="3 3" opacity=".5" />
            <circle cx={xOf(hi!)} cy={yOf(hv)} r="3.2"
              fill={c.color} stroke="var(--bm-bg)" strokeWidth="1.5" />
          </>
        )}
        <text x={padL} y={H - 5} fontFamily="var(--bm-font-mono)" fontSize="8.5" fill="var(--bm-text-subtle)">day 0</text>
        <text x={W - padR} y={H - 5} textAnchor="end" fontFamily="var(--bm-font-mono)" fontSize="8.5" fill="var(--bm-text-subtle)">{maxLen - 1}</text>
      </svg>

      <div style={{
        padding: '6px 10px',
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-muted)',
        borderTop: '1px solid var(--bm-border)',
      }}>
        {hv != null
          ? `day ${hi} · ${hv} ${c.metric}`
          : `peak day ${c.peakDay} · ${Math.max(...c.data)} ${c.metric}`}
      </div>
    </div>
  );
}
